import { clearSnapshots } from './overviewSnapshot'
import type { SignOut } from '@supabase/supabase-js'
import { EDITOR_URL } from './api'
import { supabase } from './supabase'

/**
 * Puente de sesión entre dashboard y editor.
 *
 * Viven en orígenes distintos, así que cada uno guarda su sesión por separado y
 * el editor sólo pide un handoff cuando no tiene ninguna. Sin nada que los ate,
 * una sesión vieja del editor sobrevivía al cambio de cuenta de aquí y abrir un
 * proyecto acababa en «este proyecto no existe o no pertenece a tu cuenta».
 */

// El identificador de la cuenta se cachea porque los enlaces al editor se arman
// al pulsar, y ahí no se puede esperar a una promesa.
let currentUserId: string | null = null

void supabase.auth.getSession()
  .then(({ data }) => { currentUserId = data.session?.user.id ?? null })
  .catch(() => { currentUserId = null })

supabase.auth.onAuthStateChange((_event, session) => {
  currentUserId = session?.user.id ?? null
})

/**
 * Enlace al editor con la cuenta que lo abre. `query` incluye su `?`, p. ej.
 * `?pid=123&panel=pages`. El editor compara ese `uid` con su propia sesión y
 * rehace el handoff si no coinciden.
 */
export function editorHref(query: string): string {
  const url = new URL(`${EDITOR_URL}${query}`)
  if (currentUserId) url.searchParams.set('uid', currentUserId)
  return url.toString()
}

/**
 * Cierra la sesión aquí y de paso en el editor: el navegador rebota por su
 * origen, que es el único que puede borrar lo que guardó allí, y vuelve al
 * login. Cerrar sólo aquí dejaba al editor entrando con la cuenta anterior.
 */
export async function signOutEverywhere(options?: SignOut): Promise<void> {
  const { error } = await supabase.auth.signOut(options)
  // Si el cierre falló no se rebota: quien llama avisa y el usuario sigue donde
  // estaba, en vez de dar un paseo por el editor para volver igual que empezó.
  if (error) throw error
  /* Lo que el panel guardó para pintar deprisa la próxima vez no se queda para
     quien use este navegador después. */
  clearSnapshots()

  const url = new URL(EDITOR_URL)
  url.searchParams.set('signout', '1')
  url.searchParams.set('returnTo', `${window.location.origin}/login`)
  window.location.replace(url.toString())
}
